import React, { useState, useEffect } from "react";
import { Modal, Button, Radio } from "antd";
import RichEditor from "components/RichEditor";
import { Surveys } from "api/sms-api";
import i18n from "i18n";
import { isEmptyHtmlContent } from "utils/strings";

interface VocSettings {
  vocType: number;
  vocTopPageNote: string;
  vocCompletedNote: string;
  isVoc: boolean;
}

interface VocSettingModalProps {
  visible: boolean;
  survey: Surveys | null;
  settings: VocSettings | null;
  onSave: () => void;
  onCancel: () => void;
  onVocTypeChange: (value: number) => void;
  onVocTopPageNoteChange: (value: string) => void;
  onVocCompletedNoteChange: (value: string) => void;
}

const VocSettingModal: React.FC<VocSettingModalProps> = ({
  visible,
  survey,
  settings,
  onSave,
  onCancel,
  onVocTypeChange,
  onVocTopPageNoteChange,
  onVocCompletedNoteChange,
}) => {
  const [topPageNoteEditorValue, setTopPageNoteEditorValue] = useState<string>(
    ""
  );
  const [
    completedNoteEditorValue,
    setCompletedNoteEditorValue,
  ] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (visible && settings && !isInitialized) {
      const topPageNote = settings.vocTopPageNote?.trim();
      const completedNote = settings.vocCompletedNote?.trim();

      const topPageResult =
        topPageNote && !isEmptyHtmlContent(topPageNote)
          ? topPageNote
          : i18n.t("survey.defaultVocTopPageNote");

      const completedResult =
        completedNote && !isEmptyHtmlContent(completedNote)
          ? completedNote
          : i18n.t("survey.defaultVocCompletedNote");

      setTopPageNoteEditorValue(topPageResult);
      setCompletedNoteEditorValue(completedResult);

      if (settings.vocTopPageNote !== topPageResult) {
        onVocTopPageNoteChange(topPageResult);
      }
      if (settings.vocCompletedNote !== completedResult) {
        onVocCompletedNoteChange(completedResult);
      }

      setIsInitialized(true);
    } else if (!visible) {
      setIsInitialized(false);
    }
  }, [
    visible,
    settings,
    isInitialized,
    onVocTopPageNoteChange,
    onVocCompletedNoteChange,
  ]);

  const handleVocTopPageNoteChange = (value: string) => {
    setTopPageNoteEditorValue(value);
    onVocTopPageNoteChange(value);
  };

  const handleVocCompletedNoteChange = (value: string) => {
    setCompletedNoteEditorValue(value);
    onVocCompletedNoteChange(value);
  };

  const getCurrentVocType = () => settings?.vocType ?? 1;

  return (
    <Modal
      key={`${survey?.id}-${visible}`}
      title={i18n.t("vocSettings.title")}
      visible={visible}
      onOk={onSave}
      onCancel={onCancel}
      footer={[
        <Button key="ok" type="primary" onClick={onSave}>
          {i18n.t("common.save")}
        </Button>,
      ]}
    >
      {survey && (
        <>
          <p style={{ fontWeight: "bold" }}>
            {i18n.t("vocSettings.typeSettingTitle")}
          </p>
          <Radio.Group
            value={getCurrentVocType()}
            onChange={(e) => onVocTypeChange(e.target.value)}
          >
            <Radio
              value={1}
              style={{
                display: "block",
                marginBottom: "8px",
              }}
            >
              {i18n.t("vocSettings.type.1")}
            </Radio>
            <Radio value={2} style={{ display: "block" }}>
              {i18n.t("vocSettings.type.2")}
            </Radio>
          </Radio.Group>
        </>
      )}
      <div style={{ marginTop: "24px" }}>
        <p style={{ fontWeight: "bold" }}>
          {i18n.t("vocSettings.topPageNote")}
        </p>
        {isInitialized && (
          <RichEditor
            value={topPageNoteEditorValue}
            onChange={handleVocTopPageNoteChange}
          />
        )}
      </div>
      {getCurrentVocType() === 2 && (
        <div style={{ marginTop: "24px" }}>
          <p style={{ fontWeight: "bold" }}>
            {i18n.t("vocSettings.completedMessage")}
          </p>
          {isInitialized && (
            <RichEditor
              value={completedNoteEditorValue}
              onChange={handleVocCompletedNoteChange}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default VocSettingModal;
